import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Plus, Receipt, Pencil, Trash2, TrendingDown, Repeat, UserCircle2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { todayBR } from "../lib/dates";

const TYPES = [
  { v: "monthly", label: "Mensal", Icon: Repeat, color: "#A78BFA" },
  { v: "casual", label: "Casual", Icon: Calendar, color: "#60A5FA" },
  { v: "holder", label: "Titular da conta", Icon: UserCircle2, color: "#F59E0B" },
];

function formatBRL(n) {
  return `R$ ${Math.abs(n).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

export default function ExpensesPanel() {
  const [data, setData] = useState({ expenses: [], total: 0, count: 0, available_holders: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Filter state
  const [typeFilter, setTypeFilter] = useState("all"); // all|monthly|casual|holder
  const [holderFilter, setHolderFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("month"); // month|year|all
  const today = todayBR();
  const curYear = today.slice(0, 4);
  const curMonth = today.slice(0, 7);
  const monthsElapsed = parseInt(today.slice(5, 7), 10);

  // Form state
  const [expenseType, setExpenseType] = useState("casual");
  const [date, setDate] = useState(todayBR());
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [accountHolder, setAccountHolder] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/expenses");
      setData(res.data);
    } catch {
      toast.error("Erro ao carregar despesas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setExpenseType("casual");
    setDate(todayBR());
    setDayOfMonth("1");
    setAccountHolder("");
    setDescription("");
    setAmount("");
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setExpenseType(e.expense_type || "casual");
    setDate(e.date || todayBR());
    setDayOfMonth(String(e.day_of_month || 1));
    setAccountHolder(e.account_holder || "");
    setDescription(e.description);
    setAmount(String(e.amount));
    setDialogOpen(true);
  };

  const save = async () => {
    if (!description.trim()) {
      toast.error("Descrição obrigatória");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Valor deve ser maior que zero");
      return;
    }
    if (expenseType === "monthly") {
      const n = parseInt(dayOfMonth, 10);
      if (!n || n < 1 || n > 31) {
        toast.error("Dia do mês deve estar entre 1 e 31");
        return;
      }
    } else if (!date) {
      toast.error("Data obrigatória");
      return;
    }
    if (expenseType === "holder" && !accountHolder.trim()) {
      toast.error("Selecione o titular");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        expense_type: expenseType,
        description: description.trim(),
        amount: parseFloat(amount),
        date: expenseType === "monthly" ? null : date,
        day_of_month: expenseType === "monthly" ? parseInt(dayOfMonth, 10) : null,
        account_holder: expenseType === "holder" ? accountHolder.trim() : null,
      };
      if (editing) {
        await api.put(`/expenses/${editing.id}`, payload);
        toast.success("Despesa atualizada");
      } else {
        await api.post("/expenses", payload);
        toast.success("Despesa registrada");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Despesa excluída");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const filtered = useMemo(() => {
    let list = data.expenses;
    if (periodFilter !== "all") {
      const prefix = periodFilter === "month" ? curMonth : curYear;
      list = list.filter((e) => e.expense_type === "monthly" || (e.date || "").startsWith(prefix));
    }
    if (typeFilter !== "all") list = list.filter((e) => e.expense_type === typeFilter);
    if (typeFilter === "holder" && holderFilter !== "all") {
      list = list.filter((e) => e.account_holder === holderFilter);
    }
    return list;
  }, [data.expenses, typeFilter, holderFilter, periodFilter, curMonth, curYear]);

  const recurringFactor = periodFilter === "year" ? monthsElapsed : 1;
  const filteredTotal = filtered.reduce(
    (s, e) => s + (e.amount || 0) * (e.expense_type === "monthly" ? recurringFactor : 1),
    0
  );
  const monthlyRecurring = data.expenses.filter((e) => e.expense_type === "monthly");
  const monthlyTotal = monthlyRecurring.reduce((s, e) => s + (e.amount || 0), 0);

  if (loading) {
    return <div className="h-40 animate-pulse rounded-xl border border-white/10 bg-[#121212]" />;
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-6">
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[0.75rem] uppercase tracking-widest text-zinc-500">Total no filtro</span>
            <TrendingDown className="h-4 w-4 text-[#FF3B30]" />
          </div>
          <div data-testid="expenses-total" className="font-mono-num text-3xl font-bold text-[#FF3B30]">
            {formatBRL(filteredTotal)}
          </div>
          <p className="mt-1 text-[0.7rem] uppercase tracking-widest text-zinc-600">
            {periodFilter === "month" ? "Mês atual" : periodFilter === "year" ? `Ano atual · recorrentes × ${monthsElapsed}` : "Todo o período"}
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[0.75rem] uppercase tracking-widest text-zinc-500">Lançamentos</span>
            <Receipt className="h-4 w-4 text-zinc-500" />
          </div>
          <div className="font-mono-num text-3xl font-bold text-white">{filtered.length}</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-[#121212] p-5">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[0.75rem] uppercase tracking-widest text-zinc-500">Mensal recorrente</span>
            <Repeat className="h-4 w-4 text-[#A78BFA]" />
          </div>
          <div className="font-mono-num text-3xl font-bold text-[#A78BFA]">{formatBRL(monthlyTotal)}</div>
          <p className="mt-1 text-[0.7rem] uppercase tracking-widest text-zinc-600">/mês</p>
        </div>
      </div>

      {/* Actions + Filters */}
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Despesas</h2>
          <p className="text-xs text-zinc-500">
            Cadastre gastos mensais recorrentes, gastos casuais e gastos por titular da conta.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Period filter */}
          <div className="flex rounded-full border border-white/10 bg-[#121212] p-0.5">
            {[["month", "Mês atual"], ["year", "Anual"], ["all", "Todos"]].map(([v, label]) => (
              <button
                key={v}
                data-testid={`filter-period-${v}`}
                onClick={() => setPeriodFilter(v)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  periodFilter === v ? "bg-[#D4FF00] text-black" : "text-zinc-400 hover:text-white"
                }`}
                style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Type filter chips */}
          {[
            { v: "all", label: "Todos" },
            { v: "monthly", label: "Mensal" },
            { v: "casual", label: "Casual" },
            { v: "holder", label: "Titular" },
          ].map((o) => {
            const active = typeFilter === o.v;
            return (
              <button
                key={o.v}
                data-testid={`filter-type-${o.v}`}
                onClick={() => {
                  setTypeFilter(o.v);
                  if (o.v !== "holder") setHolderFilter("all");
                }}
                className="h-9 rounded-full border px-4 text-xs font-semibold"
                style={{
                  borderColor: active ? "#D4FF00" : "rgba(255,255,255,0.1)",
                  backgroundColor: active ? "#D4FF00" : "transparent",
                  color: active ? "#000" : "#D4D4D8",
                  transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease",
                }}
              >
                {o.label}
              </button>
            );
          })}

          {/* Holder subfilter (only when Titular tab active) */}
          {typeFilter === "holder" && (
            <Select value={holderFilter} onValueChange={setHolderFilter}>
              <SelectTrigger
                data-testid="filter-holder"
                className="h-9 w-[200px] border-white/10 bg-[#121212] text-xs"
              >
                <SelectValue placeholder="Todos titulares" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                <SelectItem value="all">Todos titulares</SelectItem>
                {(data.available_holders || []).map((h) => (
                  <SelectItem key={h} value={h}>{h}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Button
            data-testid="new-expense-btn"
            onClick={openNew}
            className="h-10 gap-2 rounded-full bg-[#D4FF00] px-5 font-bold text-black hover:bg-[#c4ee00]"
            style={{ transition: "background-color 0.2s ease" }}
          >
            <Plus className="h-4 w-4" />
            Nova despesa
          </Button>
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div
          data-testid="expenses-empty"
          className="flex flex-col items-center justify-center rounded-xl border border-dashed border-white/10 bg-[#121212] p-16 text-center"
        >
          <Receipt className="mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-bold text-white">
            {periodFilter === "month" ? "Nenhuma despesa neste mês" : periodFilter === "year" ? "Nenhuma despesa neste ano" : "Nenhuma despesa registrada"}
          </h3>
          <p className="mt-1 max-w-sm text-sm text-zinc-500">
            Registre uma despesa para começar a acompanhar seus gastos.
          </p>
        </div>
      ) : (
        <>
          {/* MOBILE CARDS */}
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {filtered.map((e) => {
              const typeMeta = TYPES.find((t) => t.v === e.expense_type) || TYPES[1];
              const whenLabel =
                e.expense_type === "monthly"
                  ? `Todo dia ${String(e.day_of_month).padStart(2, "0")}`
                  : formatDate(e.date);
              return (
                <div
                  key={e.id}
                  data-testid={`expense-card-${e.id}`}
                  className="rounded-xl border border-white/10 bg-[#121212] p-4"
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.7rem] font-semibold"
                      style={{
                        borderColor: `${typeMeta.color}55`,
                        backgroundColor: `${typeMeta.color}12`,
                        color: typeMeta.color,
                      }}
                    >
                      <typeMeta.Icon className="h-3 w-3" />
                      {typeMeta.label}
                    </span>
                    <span className="font-mono-num text-xs text-zinc-400">{whenLabel}</span>
                  </div>
                  <div className="mb-2 text-sm font-semibold text-white">{e.description}</div>
                  {e.account_holder && (
                    <div className="mb-2 text-xs text-zinc-500">Titular: {e.account_holder}</div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="font-mono-num text-lg font-bold text-[#FF3B30]">
                      -{formatBRL(e.amount)}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        data-testid={`edit-expense-mobile-${e.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(e)}
                        className="h-9 w-9 text-zinc-400 hover:bg-white/5 hover:text-white"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`delete-expense-mobile-${e.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => setConfirmId(e.id)}
                        className="h-9 w-9 text-zinc-400 hover:bg-red-500/10 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* DESKTOP TABLE */}
          <div className="hidden overflow-hidden rounded-xl border border-white/10 bg-[#121212] md:block">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead className="border-b border-white/10 bg-black/40">
              <tr className="text-left">
                <th className="px-5 py-3 text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Quando</th>
                <th className="px-5 py-3 text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Tipo</th>
                <th className="px-5 py-3 text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Descrição</th>
                <th className="px-5 py-3 text-right text-[0.7rem] font-medium uppercase tracking-widest text-zinc-500">Valor</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const typeMeta = TYPES.find((t) => t.v === e.expense_type) || TYPES[1];
                const whenLabel =
                  e.expense_type === "monthly"
                    ? `Todo dia ${String(e.day_of_month).padStart(2, "0")}`
                    : formatDate(e.date);
                return (
                  <tr
                    key={e.id}
                    data-testid={`expense-row-${e.id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                    style={{ transition: "background-color 0.15s ease" }}
                  >
                    <td className="px-5 py-4 font-mono-num text-sm text-zinc-300">{whenLabel}</td>
                    <td className="px-5 py-4">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.75rem] font-semibold"
                        style={{
                          borderColor: `${typeMeta.color}55`,
                          backgroundColor: `${typeMeta.color}12`,
                          color: typeMeta.color,
                        }}
                      >
                        <typeMeta.Icon className="h-3 w-3" />
                        {typeMeta.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-sm text-white">
                      {e.description}
                      {e.account_holder && (
                        <div className="mt-0.5 text-xs text-zinc-500">Titular: {e.account_holder}</div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right font-mono-num text-sm font-bold text-[#FF3B30]">
                      -{formatBRL(e.amount)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          data-testid={`edit-expense-${e.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(e)}
                          className="h-8 w-8 text-zinc-500 hover:bg-white/5 hover:text-white"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          data-testid={`delete-expense-${e.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => setConfirmId(e.id)}
                          className="h-8 w-8 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
        </>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="expense-form-dialog" className="border-white/10 bg-[#121212]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-white">
              {editing ? "Editar despesa" : "Nova despesa"}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Escolha o tipo e preencha os dados abaixo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Type selector */}
            <div>
              <Label className="mb-2 block text-xs uppercase tracking-widest text-zinc-500">Tipo</Label>
              <div className="grid grid-cols-3 gap-2">
                {TYPES.map((t) => {
                  const active = expenseType === t.v;
                  return (
                    <button
                      key={t.v}
                      type="button"
                      data-testid={`type-${t.v}`}
                      onClick={() => setExpenseType(t.v)}
                      className="flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold"
                      style={{
                        borderColor: active ? t.color : "rgba(255,255,255,0.1)",
                        backgroundColor: active ? `${t.color}20` : "transparent",
                        color: active ? t.color : "#A1A1AA",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <t.Icon className="h-3.5 w-3.5" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Date OR day of month */}
              {expenseType === "monthly" ? (
                <div>
                  <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
                    Dia do mês (1–31)
                  </Label>
                  <Input
                    data-testid="expense-day-input"
                    type="number"
                    min="1"
                    max="31"
                    value={dayOfMonth}
                    onChange={(e) => setDayOfMonth(e.target.value)}
                    className="bg-black/40 font-mono-num"
                  />
                </div>
              ) : (
                <div>
                  <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Data</Label>
                  <Input
                    data-testid="expense-date-input"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="bg-black/40 font-mono-num"
                  />
                </div>
              )}
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Valor (R$)</Label>
                <Input
                  data-testid="expense-amount-input"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="bg-black/40 font-mono-num"
                />
              </div>
            </div>

            {/* Account holder (only for holder type) */}
            {expenseType === "holder" && (
              <div>
                <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Titular da conta</Label>
                {data.available_holders && data.available_holders.length > 0 ? (
                  <Select value={accountHolder} onValueChange={setAccountHolder}>
                    <SelectTrigger
                      data-testid="expense-holder-select"
                      className="bg-black/40"
                    >
                      <SelectValue placeholder="Selecione o titular" />
                    </SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#121212] text-zinc-200">
                      {data.available_holders.map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <>
                    <Input
                      data-testid="expense-holder-input"
                      value={accountHolder}
                      onChange={(e) => setAccountHolder(e.target.value)}
                      placeholder="Nome do titular"
                      className="bg-black/40"
                    />
                    <p className="mt-1 text-[0.7rem] text-zinc-600">
                      Cadastre bancas na aba &quot;Bancas&quot; para escolher rapidamente.
                    </p>
                  </>
                )}
              </div>
            )}

            <div>
              <Label className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">Descrição</Label>
              <Input
                data-testid="expense-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={
                  expenseType === "monthly"
                    ? "Ex: Assinatura scanner, plano..."
                    : expenseType === "holder"
                    ? "Ex: Saque titular João"
                    : "Ex: Curso, imposto, taxa..."
                }
                className="bg-black/40"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-zinc-400 hover:text-white">
              Cancelar
            </Button>
            <Button
              data-testid="save-expense-btn"
              onClick={save}
              disabled={saving}
              className="bg-[#D4FF00] font-bold text-black hover:bg-[#c4ee00]"
              style={{ transition: "background-color 0.2s ease" }}
            >
              {saving ? "Salvando..." : editing ? "Atualizar" : "Registrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent className="border-white/10 bg-[#121212]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir despesa?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-expense-btn"
              onClick={() => {
                doDelete(confirmId);
                setConfirmId(null);
              }}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
